import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Shield, ShieldCheck, ClipboardList, BookOpen, Library, Megaphone, LogOut, User2, Trophy,
  Settings as SettingsIcon, UserCog, ClipboardCheck, FileEdit, Crown, UserPlus, Briefcase,
  Menu, X, GraduationCap, Boxes, HeartHandshake, Sparkles, ChevronRight, Lightbulb, MessageCircle, CalendarCheck,
  DoorOpen, CalendarRange, Gauge,
} from "lucide-react";
import AnnouncementPopup from "@/components/AnnouncementPopup";
import InstallPrompt from "@/components/InstallPrompt";
import MikeWidget from "@/components/MikeWidget";
import { UserMenu } from "@/components/UserMenu";
import WelcomeTour from "@/components/WelcomeTour";
import { SearchTrigger, SearchPalette } from "@/components/GlobalSearch";

/* Sidebar sections, rendered in this order. Items whose `group` is "account"
   live in the top-right user menu instead of the sidebar. */
const GROUPS = [
  { key: "main", label: null },
  { key: "competition", label: "Coopetição" },
  { key: "activity", label: "A minha atividade" },
  { key: "people", label: "Pessoas" },
  { key: "manage", label: "Gestão" },
  { key: "resources", label: "Recursos" },
];

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", group: "main", roles: ["admin", "coach", "consultant", "staff"] },

  { to: "/rankings", label: "Rankings", icon: Trophy, testid: "nav-rankings", group: "competition", roles: ["admin", "coach", "consultant", "staff"] },
  { to: "/hall-of-fame", label: "Hall of Fame", icon: Crown, testid: "nav-hof", group: "competition", roles: ["admin", "coach", "consultant", "staff"] },
  { to: "/roster", label: "Plantel da Liga", icon: ClipboardCheck, testid: "nav-roster", group: "competition", roles: ["admin", "coach", "consultant", "staff"] },
  { to: "/selection", label: "Seleção dos 32", icon: UserCog, testid: "nav-selection", group: "competition", roles: ["admin"] },
  { to: "/captain", label: "Capitão da Semana", icon: Crown, testid: "nav-captain", group: "competition", roles: ["consultant"] },

  { to: "/self-report", label: "Reportar Atividade", icon: FileEdit, testid: "nav-self", group: "activity", roles: ["consultant"] },
  { to: "/o-meu-desempenho", label: "O Meu Desempenho", icon: Gauge, testid: "nav-my-performance", group: "activity", roles: ["consultant", "admin"] },
  { to: "/recruitment", label: "Recrutamento", icon: UserPlus, testid: "nav-recruit", group: "activity", roles: ["admin", "coach", "consultant"] },
  { to: "/training", label: "Auto-Treino", icon: GraduationCap, testid: "nav-training", group: "activity", roles: ["admin", "coach", "consultant", "staff"] },
  { to: "/coach", label: "Presenças & Secretário", icon: ClipboardCheck, testid: "nav-coach", group: "activity", roles: ["coach"] },
  { to: "/my-consultants", label: "Os Meus Consultores", icon: HeartHandshake, testid: "nav-my-consultants", group: "activity", roles: ["coach", "admin"] },

  { to: "/directory", label: "Diretório KW Lead", icon: Users, testid: "nav-directory", group: "people", roles: ["admin", "coach", "consultant", "staff"] },
  { to: "/salas", label: "Marcação de Salas", icon: DoorOpen, testid: "nav-rooms", group: "resources", roles: ["admin", "coach", "staff", "consultant"] },
  { to: "/escala", label: "Escala de Serviço", icon: CalendarRange, testid: "nav-duty", group: "resources", roles: ["admin", "coach", "staff", "consultant"] },
  { to: "/meu-clube", label: "O Meu Clube", icon: Boxes, testid: "nav-my-club", group: "people", roles: ["admin", "coach", "staff", "consultant"], when: (u) => !!u?.club || ["admin", "staff"].includes(u?.role) },
  { to: "/composition", label: "Composição KW Lead", icon: Boxes, testid: "nav-composition", group: "people", roles: ["admin", "coach", "staff"] },
  { to: "/players", label: "Consultores", icon: Users, testid: "nav-players", group: "people", roles: ["admin"] },
  { to: "/assistants", label: "Assistentes", icon: HeartHandshake, testid: "nav-assistants", group: "people", roles: ["admin"] },
  { to: "/teams", label: "Equipas KW", icon: Shield, testid: "nav-teams", group: "people", roles: ["admin"] },
  { to: "/coaches", label: "Secretários", icon: ShieldCheck, testid: "nav-coaches", group: "people", roles: ["admin"] },
  { to: "/staff", label: "Staff KW Lead", icon: Briefcase, testid: "nav-staff", group: "people", roles: ["admin"] },
  { to: "/users", label: "Utilizadores", icon: UserCog, testid: "nav-users", group: "people", roles: ["admin"] },

  { to: "/weekly-entry", label: "Registo Semanal", icon: ClipboardList, testid: "nav-weekly", group: "manage", roles: ["admin"] },
  { to: "/applications", label: "Candidaturas", icon: Sparkles, testid: "nav-applications-admin", group: "manage", roles: ["admin"] },
  { to: "/reunioes-semanais", label: "Reuniões semanais", icon: CalendarCheck, testid: "nav-weekly-sessions", group: "manage", roles: ["admin", "staff", "coach", "consultant"], when: (u) => u?.role !== "consultant" || u?.secretary_club || u?.president_club || u?.is_session_captain },
  { to: "/mapa-sessoes", label: "Mapa das Sessões", icon: CalendarCheck, testid: "nav-sessions-map", group: "manage", roles: ["admin", "staff", "coach"] },
  { to: "/convocations", label: "Convocatórias e Formações", icon: CalendarCheck, testid: "nav-convocations", group: "manage", roles: ["admin", "staff", "coach"] },
  { to: "/comunicados", label: "Comunicados", icon: Megaphone, testid: "nav-communiques", group: "manage", roles: ["admin", "staff", "coach", "consultant"], when: (u) => u?.role !== "consultant" || u?.leadership === "president" },
  { to: "/mike-chats", label: "Conversas do Mike", icon: MessageCircle, testid: "nav-mike-chats", group: "manage", roles: ["admin"] },
  { to: "/platforms-assign", label: "Plataformas (atribuir)", icon: Boxes, testid: "nav-platforms-assign", group: "manage", roles: ["admin", "staff"], when: (u) => u?.role === "admin" || u?.is_platforms_manager },
  { to: "/settings", label: "Configuração", icon: SettingsIcon, testid: "nav-settings", group: "manage", roles: ["admin"] },

  { to: "/rules", label: "Regras", icon: BookOpen, testid: "nav-rules", group: "resources", roles: ["admin", "coach", "consultant", "staff"] },
  { to: "/biblioteca", label: "Biblioteca de Estudo", icon: Library, testid: "nav-library", group: "resources", roles: ["admin", "coach", "consultant", "staff"] },
  { to: "/eureka", label: "EUREKA", icon: Lightbulb, testid: "nav-eureka", group: "resources", roles: ["admin", "coach", "consultant", "staff"] },
  { to: "/teste-mbti", label: "Teste MBTI", icon: Sparkles, testid: "nav-mbti-test", group: "resources", roles: ["admin", "coach", "consultant", "staff"] },

  // Top-right account menu
  { to: "/my-profile", label: "O Meu Perfil", icon: User2, testid: "nav-profile", group: "account", roles: ["consultant", "coach", "staff"] },
  { to: "/apply", label: "Candidatar-me", icon: Sparkles, testid: "nav-apply", group: "account", roles: ["consultant", "coach", "staff"] },
  { to: "/eureka", label: "Submeter Ideia (EUREKA)", icon: Lightbulb, testid: "nav-eureka-account", group: "account", roles: ["admin", "consultant", "coach", "staff"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role || "consultant";
  const visible = NAV.filter((n) => {
    if (!n.roles.includes(role)) return false;
    // Custom predicate (e.g. staff with is_platforms_manager flag)
    if (n.when && !n.when(user)) return false;
    return true;
  });
  const accountItems = visible.filter((n) => n.group === "account");
  const sections = GROUPS
    .map((g) => ({ ...g, items: visible.filter((n) => n.group === g.key) }))
    .filter((g) => g.items.length > 0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  const activeGroup = visible.find((n) => n.to === location.pathname)?.group || "main";
  const [openGroups, setOpenGroups] = useState({ [activeGroup]: true });
  const toggleGroup = (key) => setOpenGroups((p) => ({ ...p, [key]: !p[key] }));

  // Walking into a page always reveals its own area; the rest stay as left.
  useEffect(() => {
    setOpenGroups((p) => (p[activeGroup] ? p : { ...p, [activeGroup]: true }));
  }, [activeGroup]);

  // Lock body scroll while drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const currentLabel = visible.find((n) => n.to === location.pathname)?.label
    || (location.pathname === "/" ? "Dashboard" : "");

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-sm transition-colors font-display uppercase tracking-wider text-sm font-bold ${
      isActive
        ? "bg-[#1A1A1A] text-white border-l-2 border-[#E50000]"
        : "text-[#A3A3A3] hover:text-white hover:bg-[#141414]"
    }`;

  const SidebarContent = (
    <>
      <div className="p-6 border-b border-[#1F1F1F] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E50000] flex items-center justify-center kw-glow-red">
            <span className="font-display font-black text-xl leading-none">KW</span>
          </div>
          <div>
            <div className="font-display text-xl font-black uppercase tracking-tight leading-none">Lead Growth</div>
            <div className="font-display text-xl font-black uppercase tracking-tight leading-none text-[#E50000]">League</div>
          </div>
        </div>
        <button
          className="md:hidden text-[#A3A3A3] hover:text-white"
          onClick={() => setDrawerOpen(false)}
          aria-label="Fechar menu"
          data-testid="sidebar-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 p-3 overflow-y-auto">
        {sections.map((g) => {
          const isOpen = !g.label || !!openGroups[g.key];
          const activeHere = g.items.some((n) => n.to === location.pathname);
          return (
            <div key={g.key} className="mb-1.5 last:mb-0" data-testid={`nav-group-${g.key}`}>
              {g.label && (
                <button onClick={() => toggleGroup(g.key)}
                  className="w-full flex items-center gap-2 px-4 pt-3.5 pb-2 text-[11px] uppercase tracking-[0.18em] font-bold text-[#B5B5B5] hover:text-white transition-colors"
                  data-testid={`nav-group-toggle-${g.key}`}>
                  <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
                  <span className="flex-1 text-left">{g.label}</span>
                  {!isOpen && (
                    <span className={`text-[11px] tabular-nums ${activeHere ? "text-[#E50000]" : "text-[#666]"}`}>
                      {g.items.length}
                    </span>
                  )}
                </button>
              )}
              {isOpen && (
                <div className="space-y-0.5">
                  {g.items.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={closeDrawer}
                      data-testid={n.testid} className={navLinkClass}>
                      <n.icon className="w-4 h-4 shrink-0" />
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Account links are in the top-right menu on desktop; keep them reachable here on mobile. */}
        {accountItems.length > 0 && (
          <div className="md:hidden mt-2 pt-1 border-t border-[#1F1F1F]" data-testid="nav-group-account">
            <button onClick={() => toggleGroup("account")}
              className="w-full flex items-center gap-2 px-4 pt-3.5 pb-2 text-[11px] uppercase tracking-[0.18em] font-bold text-[#B5B5B5] hover:text-white transition-colors"
              data-testid="nav-group-toggle-account">
              <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${openGroups.account ? "rotate-90" : ""}`} />
              <span className="flex-1 text-left">A minha conta</span>
              {!openGroups.account && <span className="text-[11px] text-[#666] tabular-nums">{accountItems.length}</span>}
            </button>
            {openGroups.account && (
              <div className="space-y-0.5">
                {accountItems.map((n) => (
                  <NavLink key={n.to} to={n.to} onClick={closeDrawer}
                    data-testid={`${n.testid}-mobile`} className={navLinkClass}>
                    <n.icon className="w-4 h-4 shrink-0" />
                    {n.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      <div className="md:hidden p-3 border-t border-[#1F1F1F]">
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          data-testid="logout-btn-mobile"
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#A3A3A3] hover:text-white hover:bg-[#141414] rounded-sm"
        >
          <LogOut className="w-4 h-4" /> Sair
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen md:h-screen md:flex md:overflow-hidden bg-[#0A0A0A] text-white">
      {/* Mobile top bar */}
      <header
        className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 border-b border-[#1F1F1F] bg-[#0A0A0A]"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingLeft: "max(1rem, env(safe-area-inset-left))",
          paddingRight: "max(1rem, env(safe-area-inset-right))",
          minHeight: "calc(3.5rem + env(safe-area-inset-top))",
        }}
        data-testid="mobile-topbar"
      >
        <button
          onClick={() => setDrawerOpen(true)}
          className="text-white p-3 -ml-3"
          aria-label="Abrir menu"
          data-testid="sidebar-open-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-7 h-7 bg-[#E50000] flex items-center justify-center shrink-0">
            <span className="font-display font-black text-sm leading-none">KW</span>
          </div>
          <div className="font-display text-sm font-black uppercase tracking-tight truncate">
            {currentLabel || "Lead Growth League"}
          </div>
        </div>
        <SearchTrigger onOpen={() => setSearchOpen(true)} compact />
        <UserMenu items={accountItems} compact />
      </header>

      {/* Mobile drawer + backdrop */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-opacity duration-200 ${
          drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden={!drawerOpen}
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      </div>
      <aside
        className={`md:hidden fixed top-0 left-0 bottom-0 z-50 w-72 max-w-[85vw] border-r border-[#1F1F1F] bg-[#0A0A0A] flex flex-col transform transition-transform duration-200 ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        data-testid="sidebar-mobile"
      >
        {SidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex w-64 shrink-0 border-r border-[#1F1F1F] bg-[#0A0A0A] flex-col"
        data-testid="sidebar"
      >
        {SidebarContent}
      </aside>

      <main className="flex-1 flex flex-col min-w-0 md:h-screen md:overflow-y-auto" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom))" }}>
        {/* Desktop sticky page-title bar — mirrors the mobile topbar so the user
            always knows which page they're on when scrolling long content. */}
        <div
          className="hidden md:flex sticky top-0 z-20 items-center gap-3 px-6 lg:px-10 h-14 border-b border-[#1F1F1F] bg-[#0A0A0A]/85 backdrop-blur-md"
          data-testid="desktop-page-header"
        >
          <div className="w-6 h-6 bg-[#E50000] flex items-center justify-center flex-shrink-0">
            <span className="font-display font-black text-[11px] leading-none">KW</span>
          </div>
          <div className="font-display text-sm font-black uppercase tracking-widest truncate" data-testid="desktop-page-title">
            {currentLabel || "Lead Growth League"}
          </div>
          <div className="flex-1 flex justify-center px-6">
            <SearchTrigger onOpen={() => setSearchOpen(true)} />
          </div>
          <UserMenu items={accountItems} />
        </div>
        <div className="max-w-[1600px] mx-auto w-full p-4 md:p-10">{children}</div>
      </main>
      <AnnouncementPopup />
      <InstallPrompt />
      <MikeWidget />
      <WelcomeTour />
      <SearchPalette open={searchOpen} setOpen={setSearchOpen} pages={visible} />
    </div>
  );
}
